/** Elenco delle segnalazioni con filtri (nell'URL, così il link si condivide) ed export xlsx. */
import { Button, Checkbox, Input, Select, Space, Table, Tag } from "antd";
import { DownloadOutlined, PlusOutlined } from "@ant-design/icons";
import { Link, useSearchParams } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { SEVERITA, STATI_SEGNALAZIONE, type ListeDto, type PaginaDto, type SegnalazioneRigaDto } from "@qa/shared";
import { Pro09Tag } from "./scheda/Pro09";
import { api, download } from "../../api/client";
import { usePuo } from "../../lib/permessi";
import { Flag, Owner, PageHead, Prop, Sev, StatoRf, Urgente } from "../../components/ui";
import { messaggioErrore } from "../../i18n/errori";
import { useStretto } from "../../lib/schermo";
import { palette } from "../../theme";
import { Coppia, ElencoSchede } from "../../components/Elenco";

const PAGINA = 50;

/** Tipo di segnalazione (scritto, altrimenti proposto) e fonte. */
export function TipiCella({ r }: { r: SegnalazioneRigaDto }) {
  const { t } = useTranslation();
  const proposto = !r.tipoSegnalazione ? r.classificazioneProposta?.tipoSegnalazione : null;
  return (
    <>
      {r.tipoSegnalazione ?? proposto ?? "—"}
      {proposto && <Prop>{t("comune.proposto")}</Prop>}
      {r.fonte && <div className="muted small">{r.fonte}</div>}
    </>
  );
}

export default function SegnalazioniPage() {
  const { t } = useTranslation();
  const puo = usePuo();
  const stretto = useStretto();
  const [sp, setSp] = useSearchParams();
  const f = {
    q: sp.get("q") ?? undefined,
    severita: sp.get("severita") ?? undefined,
    stato: sp.get("stato") ?? undefined,
    societa: sp.get("societa") ?? undefined,
    tipo: sp.get("tipo") ?? undefined,
    tipoSegnalazione: sp.get("tipoSegnalazione") ?? undefined,
    fonte: sp.get("fonte") ?? undefined,
    inRitardo: sp.get("inRitardo") === "1" ? true : undefined,
    vip: sp.get("vip") === "1" ? true : undefined,
    urgenti: sp.get("urgenti") === "1" ? true : undefined,
    aperte: sp.get("aperte") === "1" ? true : undefined,
    fornitoreId: sp.get("fornitoreId") ?? undefined,
  };
  const pagina = Number(sp.get("p") ?? "1") || 1;
  const set = (k: string, v: string | undefined) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v);
    else n.delete(k);
    n.delete("p");
    setSp(n, { replace: true });
  };
  const liste = useQuery({ queryKey: ["liste"], queryFn: () => api.get<ListeDto>("/liste"), staleTime: 300_000 });
  const q = useQuery({
    queryKey: ["segnalazioni", f, pagina],
    queryFn: () => api.get<PaginaDto<SegnalazioneRigaDto>>("/redflag/segnalazioni", { ...f, limit: PAGINA, offset: (pagina - 1) * PAGINA }),
    placeholderData: keepPreviousData,
  });
  const voci = (lista: string) => (liste.data?.voci ?? []).filter((v) => v.lista === lista && v.attiva).map((v) => v.valore);
  const societa = voci("societa");

  return (
    <>
      <PageHead
        crumbs={[{ label: t("portale.nome"), to: "/" }, { label: t("menu.gruppoRedflag"), to: "/redflag" }, { label: t("menu.segnalazioni") }]}
        title={t("redflag.elenco.titolo")}
        sub={q.data ? t("redflag.elenco.sottotitolo", { n: q.data.totale }) : undefined}
        extra={
          <>
            <Button icon={<DownloadOutlined />} onClick={() => download("/redflag/segnalazioni/export.xlsx", f)}>
              {t("azioni.esportaXlsx")}
            </Button>
            {puo("rf.crea") && (
              <Link to="/redflag/segnalazioni/nuova">
                <Button type="primary" icon={<PlusOutlined />}>
                  {t("redflag.cruscotto.nuova")}
                </Button>
              </Link>
            )}
          </>
        }
      />
      <Space wrap style={{ marginBottom: 14 }}>
        <Input.Search
          allowClear
          defaultValue={f.q}
          placeholder={t("redflag.elenco.cerca")}
          aria-label={t("redflag.elenco.cerca")}
          onSearch={(v) => set("q", v.trim() || undefined)}
          style={{ width: stretto ? "100%" : 260 }}
        />
        <Select
          allowClear
          placeholder={t("redflag.elenco.tutteSeverita")}
          value={f.severita}
          onChange={(v) => set("severita", v)}
          style={{ width: stretto ? "calc(50% - 4px)" : 160 }}
          options={SEVERITA.map((s) => ({ value: s, label: s }))}
        />
        <Select
          allowClear
          placeholder={t("redflag.elenco.tuttiStati")}
          value={f.stato}
          onChange={(v) => set("stato", v)}
          style={{ width: stretto ? "calc(50% - 4px)" : 160 }}
          options={STATI_SEGNALAZIONE.map((s) => ({ value: s, label: t(`dominio.statoRf.${s}`) }))}
        />
        <Select
          allowClear
          placeholder={t("redflag.elenco.tutteSocieta")}
          value={f.societa}
          onChange={(v) => set("societa", v)}
          style={{ width: stretto ? "calc(50% - 4px)" : 170 }}
          options={societa.map((s) => ({ value: s, label: s }))}
        />
        <Select
          allowClear
          placeholder={t("redflag.elenco.tuttiTipiSegnalazione")}
          value={f.tipoSegnalazione}
          onChange={(v) => set("tipoSegnalazione", v)}
          style={{ width: stretto ? "calc(50% - 4px)" : 180 }}
          options={voci("tipo_segnalazione").map((s) => ({ value: s, label: s }))}
        />
        <Select
          allowClear
          placeholder={t("redflag.elenco.tutteFonti")}
          value={f.fonte}
          onChange={(v) => set("fonte", v)}
          style={{ width: stretto ? "calc(50% - 4px)" : 180 }}
          options={[...voci("fonte"), "File red flag (import)"].map((s) => ({ value: s, label: s }))}
        />
        <Checkbox checked={!!f.inRitardo} onChange={(e) => set("inRitardo", e.target.checked ? "1" : undefined)}>
          {t("redflag.elenco.soloRitardo")}
        </Checkbox>
        <Checkbox checked={!!f.vip} onChange={(e) => set("vip", e.target.checked ? "1" : undefined)}>
          {t("redflag.elenco.soloVip")}
        </Checkbox>
        <Checkbox checked={!!f.urgenti} onChange={(e) => set("urgenti", e.target.checked ? "1" : undefined)}>
          {t("redflag.elenco.soloUrgenti")}
        </Checkbox>
        <Checkbox checked={!!f.aperte} onChange={(e) => set("aperte", e.target.checked ? "1" : undefined)}>
          {t("redflag.elenco.soloAperte")}
        </Checkbox>
      </Space>
      {stretto ? (
        <ElencoSchede
          righe={q.data?.righe ?? []}
          chiave={(r) => r.id}
          loading={q.isFetching}
          vuoto={q.isError ? messaggioErrore(q.error) : t("redflag.elenco.vuoto")}
          scheda={(r) => (
            <Link to={`/redflag/segnalazioni/${r.id}`} style={{ color: "inherit", display: "block" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                <span className="mono" style={{ color: palette.primary }}>
                  {r.codice}
                </span>
                <span>
                  <Sev s={r.severita} />
                  {r.urgente && <Urgente />}
                </span>
              </div>
              <div style={{ marginTop: 4 }}>
                <b>{r.cliente}</b>
                <Flag f={r.flag} />
                {r.progetto && <div className="muted small">{r.progetto}</div>}
              </div>
              <Coppia l={t("redflag.elenco.stato")}>
                <StatoRf s={r.stato} />
              </Coppia>
              <Coppia l={t("redflag.elenco.valore")}>
                <span className="num" data-sensitive>{r.valoreTesto ?? "—"}</span>
              </Coppia>
              <Coppia l={t("redflag.elenco.tipo")}>
                <TipiCella r={r} />
              </Coppia>
              <Coppia l={t("redflag.elenco.prossimo")}>
                <Pro09Tag p={r.pro09} />
              </Coppia>
              <Coppia l={t("redflag.elenco.owner")}>
                <Owner o={r.ownerQualita} />
              </Coppia>
            </Link>
          )}
        />
      ) : (
        <Table<SegnalazioneRigaDto>
          rowKey="id"
          size="middle"
          loading={q.isFetching}
          dataSource={q.data?.righe ?? []}
          scroll={{ x: 1100 }}
          locale={{ emptyText: q.isError ? messaggioErrore(q.error) : t("redflag.elenco.vuoto") }}
          pagination={{
            current: pagina,
            pageSize: PAGINA,
            total: q.data?.totale ?? 0,
            showSizeChanger: false,
            hideOnSinglePage: true,
            onChange: (p) => {
              const n = new URLSearchParams(sp);
              n.set("p", String(p));
              setSp(n);
            },
          }}
          columns={[
            {
              title: t("redflag.elenco.codice"),
              key: "codice",
              width: 90,
              render: (_, r) => (
                <Link className="mono" to={`/redflag/segnalazioni/${r.id}`}>
                  {r.codice}
                </Link>
              ),
            },
            { title: t("redflag.elenco.data"), key: "data", width: 110, render: (_, r) => <span className="num">{r.dataTesto ?? r.dataSegnalazione ?? "—"}</span> },
            {
              title: t("redflag.elenco.cliente"),
              key: "cliente",
              render: (_, r) => (
                <>
                  <b>{r.cliente}</b>
                  {r.vip && <Tag color="gold" bordered={false} style={{ marginLeft: 6 }}>{t("redflag.vip")}</Tag>}
                  <Flag f={r.flag} />
                  {r.progetto && (
                    <>
                      <br />
                      <small className="muted">{r.progetto}</small>
                    </>
                  )}
                </>
              ),
            },
            { title: t("redflag.elenco.societa"), dataIndex: "societa", width: 120 },
            {
              title: t("redflag.elenco.severita"),
              key: "sev",
              width: 120,
              render: (_, r) => (
                <>
                  <Sev s={r.severita} />
                  {r.urgente && <Urgente />}
                </>
              ),
            },
            { title: t("redflag.elenco.valore"), key: "valore", align: "right", width: 130, render: (_, r) => <span className="num" data-sensitive>{r.valoreTesto ?? "—"}</span> },
            { title: t("redflag.elenco.tipo"), key: "tipo", width: 170, render: (_, r) => <TipiCella r={r} /> },
            { title: t("redflag.elenco.prossimo"), key: "pro09", width: 150, render: (_, r) => <Pro09Tag p={r.pro09} /> },
            {
              title: t("redflag.elenco.azioni"),
              key: "azioni",
              width: 80,
              render: (_, r) => (r.azioniAperte ? <span className={r.azioniScadute ? "miss num" : "num"}>{r.azioniAperte}</span> : <span className="muted">—</span>),
            },
            { title: t("redflag.elenco.nc"), key: "nc", width: 60, render: (_, r) => <span className="num">{r.ncTotali ? `${r.ncAperte}/${r.ncTotali}` : "—"}</span> },
            { title: t("redflag.elenco.owner"), key: "owner", width: 140, render: (_, r) => <Owner o={r.ownerQualita} /> },
            { title: t("redflag.elenco.stato"), key: "stato", width: 120, render: (_, r) => <StatoRf s={r.stato} /> },
          ]}
        />
      )}
    </>
  );
}
